// Vista del Sandbox: esta sesión lee el tenant único de ejemplo, en solo lectura.
//
// Entrar cambia las dos llaves de compañía de `localStorage` por el tenant, igual
// que el selector de compañía de un owner. Así el mapa, los stores y la API siguen
// sin saber del Sandbox. Las originales se guardan para volver a la cuenta real.
//
// El interceptor de `utils/api.ts` decide con `sandboxRequestMode` qué hace cada
// petición. El backend aplica la misma regla por su cuenta
// (`modules/sandbox/sandboxTenant.js`): esto sólo evita el viaje y explica el
// motivo. La lista de escrituras que no cambian datos es la misma en los dos.
//
// Este módulo no importa stores ni la API: lo usa el interceptor.

const VIEW_KEY = "sandboxView";
const REAL_COMPANY_KEY = "sandboxRealCompanyId";
const REAL_USER_COMPANY_KEY = "sandboxRealUserCompanyId";

export const SANDBOX_VIEW_HEADER = "x-sandbox-view";
/** Se dispara al detener una escritura: la leyenda ofrece salir a la cuenta real. */
export const SANDBOX_READ_ONLY_EVENT = "sandbox:read-only";

/** Lo de la cuenta de la persona: va con su compañía real aunque esté en la vista. */
const OWN_ACCOUNT_PATHS: RegExp[] = [
  /^\/sandbox(\/|$)/,
  /^\/access\/me$/,
  /^\/billing(\/|$)/,
  /^\/subscriptions/,
  /^\/onboarding(\/|$)/,
  /^\/tutorials(\/|$)/,
  /^\/session(\/|$)/,
  /^\/security(\/|$)/,
  /^\/auth\//,
  /^\/notifications\/(suscribe|unsuscribe)$/,
  /^\/user$/,
  /^\/users\/change-password$/,
  /^\/ai\/thread/,
];

/** POST y PUT que no cambian datos del negocio: leen el tenant. */
const READ_ONLY_WRITES: RegExp[] = [
  /^\/tracking\/addresses$/,
  /^\/reports\/background$/,
  /^\/reports\/readed(\/all)?$/,
  /^\/ai\/chat$/,
  /^\/quotes\/estimate$/,
  /^\/pos\/coupons\/validate$/,
  /^\/payslipCalc$/,
  /^\/payslipReceipt$/,
  /^\/workSettlementCalculation$/,
  /(^|\/)tickets\/quote$/,
];

export function isSandboxView(): boolean {
  return localStorage.getItem(VIEW_KEY) === "1";
}

const restore = (key: string, saved: string | null) => {
  if (saved) localStorage.setItem(key, saved);
  else localStorage.removeItem(key);
};

export function enterSandboxView(tenantId: string): void {
  if (!isSandboxView()) {
    restore(REAL_COMPANY_KEY, localStorage.getItem("companyId"));
    restore(REAL_USER_COMPANY_KEY, localStorage.getItem("userCompanyId"));
  }
  localStorage.setItem(VIEW_KEY, "1");
  localStorage.setItem("companyId", tenantId);
  localStorage.setItem("userCompanyId", tenantId);
}

export function exitSandboxView(): void {
  if (!isSandboxView()) return;
  restore("companyId", localStorage.getItem(REAL_COMPANY_KEY));
  restore("userCompanyId", localStorage.getItem(REAL_USER_COMPANY_KEY));
  clearSandboxView();
}

/** Olvida la vista sin restaurar: al cerrar sesión, o cuando el login ya puso la compañía real. */
export function clearSandboxView(): void {
  localStorage.removeItem(VIEW_KEY);
  localStorage.removeItem(REAL_COMPANY_KEY);
  localStorage.removeItem(REAL_USER_COMPANY_KEY);
}

/** Compañía real de la sesión: la que se usa para lo que es de la cuenta. */
export function realCompanyId(): string | null {
  return isSandboxView() ? localStorage.getItem(REAL_COMPANY_KEY) || localStorage.getItem(REAL_USER_COMPANY_KEY) : localStorage.getItem("companyId");
}

const pathOf = (url: string | undefined, baseURL: string): string => {
  let path = url || "";
  if (baseURL && path.startsWith(baseURL)) path = path.slice(baseURL.length);
  path = path.split("?")[0];
  if (!path.startsWith("/")) path = `/${path}`;
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
};

export type SandboxRequestMode =
  /** Sin vista, o petición de la cuenta: va con la compañía real y sin el encabezado. */
  | "own-account"
  /** Lee el tenant: lleva el encabezado. */
  | "view"
  /** Escritura de negocio: no sale y se ofrece salir de la vista. */
  | "blocked";

export function sandboxRequestMode(method: string | undefined, url: string | undefined, baseURL = ""): SandboxRequestMode {
  if (!isSandboxView()) return "own-account";
  const path = pathOf(url, baseURL);
  if (OWN_ACCOUNT_PATHS.some((re) => re.test(path))) return "own-account";
  const verb = (method || "get").toLowerCase();
  if (verb === "get" || verb === "head" || verb === "options") return "view";
  return READ_ONLY_WRITES.some((re) => re.test(path)) ? "view" : "blocked";
}

export function announceSandboxReadOnly(): void {
  window.dispatchEvent(new Event(SANDBOX_READ_ONLY_EVENT));
}
